import * as Yup from "yup";

// Limits mirror the backend contract for POST /policyWallet/addMoney.
export const MAX_AMOUNT = 10000000; // 1,00,00,000
export const MAX_REFERENCE_LENGTH = 50;
export const MAX_REMARKS_LENGTH = 200;
export const MAX_RECEIPT_BYTES = 5 * 1024 * 1024;
export const REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;
export const RECEIPT_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"];
export const RECEIPT_ACCEPT = RECEIPT_TYPES.join(",");
export const QUICK_AMOUNTS = [500, 1000, 5000];

export const PAYMENT_MODES = [
  { value: "CASH", label: "Cash", referenceLabel: "Reference no." },
  { value: "BANK_TRANSFER", label: "Bank transfer", referenceLabel: "UTR / cheque no." },
  { value: "UPI", label: "UPI", referenceLabel: "UTR / cheque no." },
  { value: "CHEQUE", label: "Cheque", referenceLabel: "UTR / cheque no." },
  { value: "OTHER", label: "Other", referenceLabel: "Reference no." },
];

export const paymentModeLabel = (value) =>
  PAYMENT_MODES.find((mode) => mode.value === value)?.label || value || "";

export const referenceLabelFor = (value) =>
  PAYMENT_MODES.find((mode) => mode.value === value)?.referenceLabel || "Reference no.";

// Same check style as canViewMasterHistory: the stored role can be a
// comma-joined list, and only the account owner (CLIENT) may add money.
export const isClientUserType = (userType) =>
  String(userType || "")
    .split(",")
    .some((type) => type.trim().toLowerCase() === "client");

export const canAddMoneyToUsers = () => isClientUserType(sessionStorage.getItem("userType"));

// The super admin (a CLIENT login whose roleId is SUPER_ADMIN) has no users of its own: it picks any admin's user.
export const isSuperAdminLogin = () => sessionStorage.getItem("roleId") === "SUPER_ADMIN";

// ---------------------------------------------------------------- amounts

// Keeps digits and one dot, at most 2 decimals, no stray leading zeros
// ("1,000" -> "1000", "007" -> "7", ".5" -> "0.5").
export const sanitizeAmountInput = (raw) => {
  const cleaned = String(raw ?? "").replace(/[^0-9.]/g, "");
  if (!cleaned) return "";
  const firstDot = cleaned.indexOf(".");
  let whole = firstDot === -1 ? cleaned : cleaned.slice(0, firstDot);
  const decimals =
    firstDot === -1 ? null : cleaned.slice(firstDot + 1).replace(/\./g, "").slice(0, 2);
  whole = whole.replace(/^0+(?=\d)/, "");
  if (decimals === null) return whole;
  return `${whole || "0"}.${decimals}`;
};

const trimTrailingDot = (value) => String(value ?? "").trim().replace(/\.$/, "");

// Integer paise, so 0.1 + 0.2 style float drift never reaches the screen.
export const toPaise = (value) => {
  const text = trimTrailingDot(value);
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return NaN;
  return Math.round(Number(text) * 100);
};

export const paiseToAmountString = (paise) => {
  const fixed = (paise / 100).toFixed(2);
  return fixed.replace(/\.?0+$/, "");
};

export const formatINR = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** "" when valid, otherwise the message to show under the field. */
export const validateAmount = (value) => {
  const text = trimTrailingDot(value);
  if (!text) return "Amount is required";
  if (/^\d+\.\d{3,}$/.test(text)) return "Maximum 2 decimal places";
  const paise = toPaise(text);
  if (Number.isNaN(paise)) return "Enter a valid amount";
  if (paise <= 0) return "Amount must be greater than 0";
  if (paise > MAX_AMOUNT * 100) return `Amount cannot exceed ₹ ${MAX_AMOUNT.toLocaleString("en-IN")}`;
  return "";
};

/** Quick-chip helper: current field text + a fixed step, as new field text. */
export const addToAmount = (current, step) => {
  const base = toPaise(current);
  return paiseToAmountString((Number.isNaN(base) ? 0 : base) + Math.round(step * 100));
};

/** Wallet balance once `amount` is credited; null while the amount is not valid. */
export const computeBalanceAfter = (currentBalance, amount) => {
  if (validateAmount(amount)) return null;
  const current = Number(currentBalance);
  const currentPaise = Number.isFinite(current) ? Math.round(current * 100) : 0;
  return (currentPaise + toPaise(amount)) / 100;
};

// -------------------------------------------------------------- user labels

export const userLabel = (user) =>
  [user?.name, user?.mobileNumber, user?.email].filter(Boolean).join(" · ");

// ------------------------------------------------------------------ receipts

export const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

/** "" when the file is acceptable (or absent), otherwise the message to show. */
export const validateReceiptFile = (file) => {
  if (!file) return "";
  const type = String(file.type || "").toLowerCase();
  if (!RECEIPT_TYPES.includes(type)) {
    return "Only JPG, PNG, GIF, WebP images or PDF files are allowed";
  }
  if (!file.size) return "This file is empty";
  if (file.size > MAX_RECEIPT_BYTES) return "File is too large — 5 MB max";
  return "";
};

// Stored proofs come as a bare S3 key on new rows and {key, fileName} on old
// top-up requests; the viewer needs the key and a display name either way.
export const proofKeyOf = (proof) => (typeof proof === "string" ? proof : proof?.key || "");

export const proofNameOf = (proof) =>
  (typeof proof === "object" && proof?.fileName) || proofKeyOf(proof).split("/").pop() || "Proof";

export const isPdfKey = (key) => /\.pdf(\?.*)?$/i.test(String(key || ""));

// ---------------------------------------------------------------- request id

export const generateRequestId = () => {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();
  if (cryptoApi && typeof cryptoApi.getRandomValues === "function") {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

// The server refuses a reused id that arrives with a different user or amount,
// so those two define "the same attempt". Anything else (remarks, proof, ...)
// may be edited between retries and still resolves to the original credit.
const fingerprintOf = ({ userId, amount }) => JSON.stringify([userId, toPaise(amount)]);

/**
 * True when the same requestId should go out again: no response reached us
 * (network error, timeout, gateway failure) so the credit may or may not have
 * happened, or the server says the first attempt is still running. After any
 * other error the id may be burnt (REQUEST_FAILED, REQUEST_ID_REUSED), so a
 * fresh one is needed before the next submit.
 */
export const shouldReuseRequestId = (response) =>
  Boolean(response && (response.noResponse || response.code === "IN_PROGRESS"));

/**
 * Idempotency key per submission attempt. Retrying the same user and amount
 * keeps the key, so a retry after a lost response cannot credit twice; a
 * different user or amount gets a new key. `reset()` after a success, a burnt
 * id, or a form reset.
 */
export const createAttemptTracker = (generate = generateRequestId) => {
  let current = null;
  return {
    idFor(details) {
      const fingerprint = fingerprintOf(details);
      if (!current || current.fingerprint !== fingerprint) {
        current = { fingerprint, id: generate() };
      }
      return current.id;
    },
    reset() {
      current = null;
    },
  };
};

export const buildAddMoneyFormData = ({ userId, adminClientId, amount, paymentMode, reference, remarks, receipt }, requestId) => {
  const formData = new FormData();
  formData.append("userId", userId);
  // The super admin's top-up names the admin the user belongs to (the server checks it); an admin never sends it.
  if (adminClientId) formData.append("adminClientId", adminClientId);
  formData.append("amount", paiseToAmountString(toPaise(amount)));
  formData.append("paymentMode", paymentMode);
  if ((reference || "").trim()) formData.append("reference", reference.trim());
  if ((remarks || "").trim()) formData.append("remarks", remarks.trim());
  formData.append("requestId", requestId);
  if (receipt) formData.append("receiptImage", receipt);
  return formData;
};

// ---------------------------------------------------------------- validation

export const addMoneySchema = Yup.object().shape({
  user: Yup.object().nullable().required("Please select a user"),
  amount: Yup.string().test("amount-rules", function amountRules(value) {
    const message = validateAmount(value);
    return message ? this.createError({ message }) : true;
  }),
  paymentMode: Yup.string()
    .oneOf(
      PAYMENT_MODES.map((mode) => mode.value),
      "Select a payment mode"
    )
    .required("Select a payment mode"),
  reference: Yup.string()
    .trim()
    .max(MAX_REFERENCE_LENGTH, `Reference cannot exceed ${MAX_REFERENCE_LENGTH} characters`),
  remarks: Yup.string()
    .trim()
    .max(MAX_REMARKS_LENGTH, `Remarks cannot exceed ${MAX_REMARKS_LENGTH} characters`),
});
